import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, FormControl,
  InputLabel, Select, MenuItem, Grid, Chip, Button, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import api from '../../services/api';

const periodLabels = {
  term1: 'Term 1 (Jan-Mar)', term2: 'Term 2 (Apr-Jun)',
  term3: 'Term 3 (Jul-Sep)', term4: 'Term 4 (Oct-Dec)',
  semester1: 'Semester 1 (Jan-Jun)', semester2: 'Semester 2 (Jul-Dec)',
  year: 'Full Year'
};

export default function ImpactReportTab({ schoolId, school }) {
  const [period, setPeriod] = useState('year');
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    const fetchImpact = async () => {
      setLoading(true); setError(null);
      try {
        const res = await api.get(`/api/schools/${schoolId}/reports/impact`, { params: { period, year } });
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load impact report');
      } finally { setLoading(false); }
    };
    fetchImpact();
  }, [schoolId, period, year]);

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Period</InputLabel>
          <Select value={period} label="Period" onChange={e => setPeriod(e.target.value)}>
            {Object.entries(periodLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Year</InputLabel>
          <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
            {[2024, 2025, 2026].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}>Print / Save PDF</Button>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <Box ref={printRef} sx={{ '@media print': { p: 2 } }}>
          <Paper sx={{ p: 3, mb: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <Typography variant="h5" fontWeight="bold">{data.school?.name || school?.name}</Typography>
            <Typography variant="body2">
              {data.school?.province} {data.school?.district && `\u2022 ${data.school.district}`}
              {data.school?.type && ` \u2022 ${data.school.type}`}
              {data.school?.quintile && ` \u2022 Quintile ${data.school.quintile}`}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>MathZA Impact Report — {periodLabels[period]} {year}</Typography>
          </Paper>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Learners', value: data.metrics.totalLearners },
              { label: 'Active Learners', value: data.metrics.activeLearners },
              { label: 'Engagement Rate', value: `${data.metrics.engagementRate}%` },
              { label: 'Questions Answered', value: (data.metrics.questionsAnswered || 0).toLocaleString() },
              { label: 'Average Accuracy', value: `${data.metrics.averageAccuracy}%` },
              { label: 'Topics Covered', value: data.metrics.topicsCovered },
            ].map((m, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight="bold" color="primary">{m.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {data.gradeBreakdown && data.gradeBreakdown.length > 0 && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Performance by Grade</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Grade</TableCell><TableCell align="center">Learners</TableCell>
                    <TableCell align="center">Questions</TableCell><TableCell align="center">Accuracy</TableCell>
                    <TableCell align="center">Topics</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {data.gradeBreakdown.map((g, i) => (
                      <TableRow key={i}>
                        <TableCell>Grade {g.grade}</TableCell>
                        <TableCell align="center">{g.learners}</TableCell>
                        <TableCell align="center">{g.questionsAttempted}</TableCell>
                        <TableCell align="center"><Chip label={`${g.accuracy}%`} size="small" color={g.accuracy >= 60 ? 'success' : g.accuracy >= 40 ? 'warning' : 'error'} variant="outlined" /></TableCell>
                        <TableCell align="center">{g.topicsCovered}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Grid container spacing={2}>
            {data.topPerformingTopics && data.topPerformingTopics.length > 0 && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="success.main">Top Performing Topics</Typography>
                  {data.topPerformingTopics.map((t, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2">Grade {t.grade} — {t.topic}</Typography>
                      <Chip label={`${t.accuracy}%`} size="small" color="success" variant="outlined" />
                    </Box>
                  ))}
                </Paper>
              </Grid>
            )}
            {data.needsAttentionTopics && data.needsAttentionTopics.length > 0 && (
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="error.main">Topics Needing Attention</Typography>
                  {data.needsAttentionTopics.map((t, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2">Grade {t.grade} — {t.topic}</Typography>
                      <Chip label={`${t.accuracy}%`} size="small" color="error" variant="outlined" />
                    </Box>
                  ))}
                </Paper>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
            Generated by MathZA (pedaplate.co.za) on {new Date().toLocaleDateString('en-ZA')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
